<template>
  <div class="email-log p-4">
    <admin-nav />
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <i class="fas fa-mail-bulk me-2"></i>
        Email Log
      </h2>
      <button class="btn btn-primary" @click="refreshLogs">
        <i class="fas fa-sync-alt me-2"></i>
        Refresh
      </button>
    </div>

    <!-- Filters Card -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fas fa-filter me-2"></i>
          Filters
        </h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-3 mb-3">
            <label class="form-label">Status</label>
            <select class="form-select" v-model="filters.status">
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Recipient</label>
            <input type="text" class="form-control" v-model="filters.recipient" placeholder="Email address">
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Date From</label>
            <input type="date" class="form-control" v-model="filters.dateFrom">
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Date To</label>
            <input type="date" class="form-control" v-model="filters.dateTo">
          </div>
        </div>
        <button class="btn btn-primary" @click="applyFilters">
          <i class="fas fa-search me-2"></i>
          Apply Filters
        </button>
        <button class="btn btn-secondary ms-2" @click="clearFilters">
          <i class="fas fa-times me-2"></i>
          Clear
        </button>
      </div>
    </div>

    <!-- Email Log Table -->
    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div v-else-if="emails.length === 0" class="text-center py-5 text-muted">
          <i class="fas fa-inbox fa-3x mb-3"></i>
          <p>No emails found matching the current filters.</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 140px;">Date</th>
                <th style="width: 100px;">Status</th>
                <th style="width: 200px;">To</th>
                <th>Subject</th>
                <th style="width: 250px;">Body Preview</th>
                <th style="width: 80px;">Attach</th>
                <th style="width: 120px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="email in emails" :key="email.id">
                <td>
                  <small>{{ formatDate(email.sentAt || email.createdAt) }}</small>
                </td>
                <td>
                  <span class="badge" :class="getStatusClass(email.status)">
                    {{ email.status }}
                  </span>
                </td>
                <td>{{ email.to }}</td>
                <td>{{ email.subject }}</td>
                <td>
                  <small class="text-muted" :title="getBodyPreviewFull(email)">{{ getBodyPreview(email) }}</small>
                </td>
                <td class="text-center">
                  <span v-if="hasAttachment(email)" class="badge bg-info" title="Has attachment">
                    <i class="fas fa-paperclip"></i>
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-primary" @click="viewEmail(email)" title="View">
                      <i class="fas fa-eye"></i>
                    </button>
                    <button class="btn btn-outline-success" @click="resendEmail(email)" title="Resend">
                      <i class="fas fa-redo"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalCount > 0">
          <div>
            Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} entries
          </div>
          <nav>
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                  Previous
                </button>
              </li>
              <li class="page-item" v-for="page in visiblePages" :key="page" :class="{ active: page === currentPage }">
                <button class="page-link" @click="changePage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- View Email Modal -->
    <div class="modal" :class="{ 'show d-block': showViewModal }" v-if="showViewModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Email Details</h5>
            <button type="button" class="btn-close" @click="showViewModal = false"></button>
          </div>
          <div class="modal-body" v-if="selectedEmail">
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>Status:</strong><br>
                <span class="badge" :class="getStatusClass(selectedEmail.status)">
                  {{ selectedEmail.status }}
                </span>
              </div>
              <div class="col-md-6">
                <strong>Sent At:</strong><br>
                {{ formatDateFull(selectedEmail.sentAt || selectedEmail.createdAt) }}
              </div>
            </div>
            <div class="mb-3">
              <strong>To:</strong><br>
              {{ selectedEmail.to }}
            </div>
            <div class="mb-3">
              <strong>Subject:</strong><br>
              {{ selectedEmail.subject }}
            </div>
            <div class="mb-3">
              <strong>Body:</strong><br>
              <div class="border p-3 bg-light" v-html="selectedEmail.body"></div>
            </div>
            <div v-if="selectedEmail.attachments && selectedEmail.attachments.length" class="mb-3">
              <strong>Attachments:</strong><br>
              <ul>
                <li v-for="(attachment, index) in selectedEmail.attachments" :key="index">
                  {{ attachment }}
                </li>
              </ul>
            </div>
            <div v-if="selectedEmail.error" class="mb-3">
              <strong>Error:</strong><br>
              <div class="alert alert-danger">{{ selectedEmail.error }}</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showViewModal = false">Close</button>
            <button type="button" class="btn btn-success" @click="resendEmail(selectedEmail)">
              <i class="fas fa-redo me-2"></i>
              Resend
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showViewModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import Http from '@/services/Http';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import AdminNav from '@/components/Shared/AdminNav.vue';

const emails = ref<any[]>([]);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(50);
const totalCount = ref(0);
const showViewModal = ref(false);
const selectedEmail = ref<any>(null);

const filters = ref({
  status: '',
  recipient: '',
  dateFrom: '',
  dateTo: ''
});

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value));

const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

const loadEmails = async () => {
  loading.value = true;
  try {
    const params: any = {
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    if (filters.value.status) params.status = filters.value.status;
    if (filters.value.recipient) params.recipient = filters.value.recipient;
    if (filters.value.dateFrom) params.dateFrom = filters.value.dateFrom;
    if (filters.value.dateTo) params.dateTo = filters.value.dateTo;
    
    const response = await Http.get('/email/logs', params);
    // Map API response to expected format (PascalCase from backend)
    emails.value = (response?.Data || []).map((e: any) => ({
      id: e.EmailLogId,
      to: e.ToAddresses,
      cc: e.CcAddresses,
      from: e.FromAddress,
      subject: e.Subject,
      bodyJson: e.BodyJson,
      status: e.Status,
      error: e.ErrorMessage,
      retryCount: e.RetryCount,
      createdAt: e.CreatedDate,
      sentAt: e.SentDate,
      lastRetryAt: e.LastRetryDate
    }));
    totalCount.value = emails.value.length;
  } catch (error) {
    console.error('Error loading email log:', error);
    Alert.notify({ text: 'Failed to load email log', type: 'error' });
  } finally {
    loading.value = false;
  }
};

const refreshLogs = () => {
  loadEmails();
};

const applyFilters = () => {
  currentPage.value = 1;
  loadEmails();
};

const clearFilters = () => {
  filters.value = {
    status: '',
    recipient: '',
    dateFrom: '',
    dateTo: ''
  };
  currentPage.value = 1;
  loadEmails();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    loadEmails();
  }
};

const viewEmail = (email: any) => {
  // Parse the body from bodyJson for display
  let body = '';
  if (email.bodyJson) {
    try {
      const bodyData = JSON.parse(email.bodyJson);
      // Check both PascalCase (from C#) and lowercase property names
      body = bodyData.Html || bodyData.html || bodyData.Text || bodyData.text || '';
    } catch {
      body = email.bodyJson;
    }
  }
  selectedEmail.value = { ...email, body };
  showViewModal.value = true;
};

const resendEmail = (email: any) => {
  Alert.confirm(
    `Resend email to ${email.to}?`,
    async () => {
      try {
        await Http.post('/admin/email/resend', { emailId: email.id });
        Toast.success('Email resent successfully!');
        showViewModal.value = false;
        selectedEmail.value = null;
        loadEmails();
      } catch (error: any) {
        console.error('Error resending email:', error);
        Alert.notify({ text: 'Failed to resend email: ' + (error.message || 'Unknown error'), type: 'error' });
      }
    },
    () => {} // No action on cancel
  );
};

const getStatusClass = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'sent': 'bg-success',
    'failed': 'bg-danger',
    'pending': 'bg-warning'
  };
  return statusMap[status?.toLowerCase()] || 'bg-secondary';
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateFull = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const getBodyPreview = (email: any): string => {
  if (!email.bodyJson) return '-';
  try {
    const bodyData = JSON.parse(email.bodyJson);
    const text = bodyData.text || bodyData.html?.replace(/<[^>]*>/g, '') || '';
    return text.length > 50 ? text.substring(0, 50) + '...' : text || '-';
  } catch {
    return '-';
  }
};

const getBodyPreviewFull = (email: any): string => {
  if (!email.bodyJson) return '';
  try {
    const bodyData = JSON.parse(email.bodyJson);
    return bodyData.text || bodyData.html?.replace(/<[^>]*>/g, '') || '';
  } catch {
    return '';
  }
};

const hasAttachment = (email: any): boolean => {
  if (!email.bodyJson) return false;
  try {
    const bodyData = JSON.parse(email.bodyJson);
    return !!(bodyData.attachment && bodyData.attachment.base64Data);
  } catch {
    return false;
  }
};

onMounted(() => {
  loadEmails();
});
</script>

<style scoped>
.email-log {
  max-width: 1400px;
  margin: 0 auto;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
